import { createFileRoute, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc } from "~/utils/trpc";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { Button } from "@quake/shared-ui/components/ui/button";
import { Input } from "@quake/shared-ui/components/ui/input";
import { ColorPicker } from "@quake/shared-ui/components/ui/color-picker";
import { Alert, AlertDescription } from "@quake/shared-ui/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@quake/shared-ui/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@quake/shared-ui/components/ui/table";
import { Separator } from "@quake/shared-ui/components/ui/separator";
import { toast } from "@quake/shared-ui/lib/sonner";
import { AttributesDialog } from "~/components/AttributesDialog";
import { DeleteConfirmationDialog } from "~/components/DeleteConfirmationDialog";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import PlusIcon from "lucide-react/dist/esm/icons/plus";
import EditIcon from "lucide-react/dist/esm/icons/edit";
import type { AttrType } from "@quake/isomorphic/dataFilters";
import { makeAttributeId } from "@quake/isomorphic";
import type { CandidateActiveStatusOption } from "@quake/isomorphic/schemas/candidates.internal.schema";

export const Route = createFileRoute("/_dashboard/$datasetSlug/data/placements/settings")({
  component: RouteComponent,
});

type PlacementConfigOption = CandidateActiveStatusOption;

const DEFAULT_PLACEMENT_ACTIVE_STATUSES: PlacementConfigOption[] = [
  { id: "pending_start", name: "Pending Start", color: "#f59e0b", order: 0, description: "Accepted and not started" },
  { id: "active", name: "Active", color: "#22c55e", order: 1, description: "Currently in placement" },
  { id: "completed", name: "Completed", color: "#3b82f6", order: 2, description: "Placement completed" },
  { id: "cancelled", name: "Cancelled", color: "#ef4444", order: 3, description: "Placement cancelled" },
];

const DEFAULT_PLACEMENT_TYPES: PlacementConfigOption[] = [
  { id: "perm", name: "Permanent", color: "#3b82f6", order: 0, description: "Permanent placement" },
  { id: "contract", name: "Contract", color: "#22c55e", order: 1, description: "Contract placement" },
  { id: "temp", name: "Temporary", color: "#f59e0b", order: 2, description: "Temporary placement" },
];

const DEFAULT_PLACEMENT_FEE_TYPES: PlacementConfigOption[] = [
  { id: "percent", name: "Percent", color: "#8b5cf6", order: 0, description: "Percentage-based fee" },
  { id: "flat", name: "Flat", color: "#14b8a6", order: 1, description: "Flat fee" },
  { id: "hourly_margin", name: "Hourly Margin", color: "#f97316", order: 2, description: "Hourly margin fee" },
];

function normalizeOptions(options: PlacementConfigOption[]) {
  return options.map((option, index) => ({
    ...option,
    id: option.id || crypto.randomUUID(),
    color: option.color || "#2196F3",
    order: index,
  }));
}

function ConfigOptionsCard({
  title,
  description,
  options,
  setOptions,
  newOption,
  setNewOption,
  addLabel,
}: {
  title: string;
  description: string;
  options: PlacementConfigOption[];
  setOptions: Dispatch<SetStateAction<PlacementConfigOption[]>>;
  newOption: { name: string; description: string; color: string };
  setNewOption: Dispatch<SetStateAction<{ name: string; description: string; color: string }>>;
  addLabel: string;
}) {
  const updateOption = (index: number, data: Partial<PlacementConfigOption>) => {
    setOptions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...data } as PlacementConfigOption;
      return normalizeOptions(next);
    });
  };

  const removeOption = (index: number) => {
    setOptions((prev) => normalizeOptions(prev.filter((_, i) => i !== index)));
  };

  const addOption = () => {
    const trimmedName = newOption.name.trim();
    if (!trimmedName) return;
    setOptions((prev) =>
      normalizeOptions([
        ...prev,
        {
          id: crypto.randomUUID(),
          name: trimmedName,
          description: newOption.description.trim() || undefined,
          color: newOption.color,
        },
      ]),
    );
    setNewOption({ name: "", description: "", color: "#2196F3" });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mt-4 flex gap-2">
          <Input
            value={newOption.name}
            onChange={(e) => setNewOption((prev) => ({ ...prev, name: e.target.value }))}
            placeholder={addLabel}
            className="w-40"
          />
          <Input
            value={newOption.description}
            onChange={(e) => setNewOption((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Description"
          />
          <ColorPicker
            selectedColor={newOption.color}
            onColorChange={(color) => setNewOption((prev) => ({ ...prev, color }))}
          />
          <Button type="button" onClick={addOption}>
            Add
          </Button>
        </div>

        <ul className="mt-4 space-y-2">
          {options.map((option, index) => (
            <li key={option.id} className="flex items-center gap-2">
              <ColorPicker
                selectedColor={option.color}
                onColorChange={(color) => updateOption(index, { color })}
              />
              <Input
                value={option.name}
                onChange={(e) => updateOption(index, { name: e.target.value })}
                className="w-40"
              />
              <Input
                value={option.description || ""}
                onChange={(e) => updateOption(index, { description: e.target.value })}
              />
              <Trash2 className="h-4 w-4 cursor-pointer" onClick={() => removeOption(index)} />
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function RouteComponent() {
  const { datasetSlug } = useParams({ from: "/_dashboard/$datasetSlug" });
  const { data: session, isLoading: isSessionLoading } = useQuery(trpc.auth.getSession.queryOptions());
  const { data: settings } = useQuery(trpc.placementsSettings.get.queryOptions());
  const update = useMutation(trpc.placementsSettings.update.mutationOptions());
  const qc = useQueryClient();

  const [attributes, setAttributes] = useState<any[]>([]);
  const [activeStatuses, setActiveStatuses] = useState<PlacementConfigOption[]>([]);
  const [placementTypes, setPlacementTypes] = useState<PlacementConfigOption[]>([]);
  const [feeTypes, setFeeTypes] = useState<PlacementConfigOption[]>([]);
  const [newStatus, setNewStatus] = useState({ name: "", description: "", color: "#2196F3" });
  const [newPlacementType, setNewPlacementType] = useState({ name: "", description: "", color: "#2196F3" });
  const [newFeeType, setNewFeeType] = useState({ name: "", description: "", color: "#2196F3" });

  const attr: AttrType = {
    id: "",
    name: "",
    description: "",
    searchable: false,
    type: "Text",
    table_reference: "",
    options: [] as { value: string; label: string; index: number }[],
    defaultValue: "",
    boundaries: { required: false, unique: false },
  };

  const entityTypes = [
    "Text",
    "Number",
    "Checkbox",
    "Date",
    "TimeStamp",
    "Select",
    "Multi Select",
    "Record",
    "Multi Record",
  ] as const;
  const optionsList = ["Select", "Multi Select"] as const;

  const [attributeDialogOpen, setAttributeDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit">("add");
  const [selectedAttribute, setSelectedAttribute] = useState(attr);
  const [attrIndex, setAttrIndex] = useState(-1);
  const [attributeToDeleteIndex, setAttributeToDeleteIndex] = useState<number | null>(null);
  const canCreateAttributes = session?.user?.role === "Administrator";

  useEffect(() => {
    setAttributes(settings?.attributes || []);
    setActiveStatuses(normalizeOptions(settings?.active_statuses ?? DEFAULT_PLACEMENT_ACTIVE_STATUSES));
    setPlacementTypes(normalizeOptions(settings?.placement_types ?? DEFAULT_PLACEMENT_TYPES));
    setFeeTypes(normalizeOptions(settings?.fee_types ?? DEFAULT_PLACEMENT_FEE_TYPES));
  }, [settings]);

  useBreadCrumbs([{ title: "Placements Settings", link: `/${datasetSlug}/data/placements/settings` }]);

  const save = () =>
    update.mutate(
      {
        attributes,
        active_statuses: normalizeOptions(activeStatuses),
        placement_types: normalizeOptions(placementTypes),
        fee_types: normalizeOptions(feeTypes),
      },
      {
        onSuccess: async () => {
          await Promise.all([
            qc.invalidateQueries(trpc.placementsSettings.get.queryFilter()),
            qc.invalidateQueries({ queryKey: trpc.placements.pathKey() }),
          ]);
          toast.success("Placement settings saved successfully");
        },
      },
    );

  const handleAddClick = () => {
    setDialogMode("add");
    setSelectedAttribute(attr);
    setAttributeDialogOpen(true);
  };

  const handleEditClick = (attribute: typeof attr, index: number) => {
    setDialogMode("edit");
    setSelectedAttribute(attribute);
    setAttrIndex(index);
    setAttributeDialogOpen(true);
  };

  const handleDialogSave = (newAttr: typeof attr) => {
    let next = [...attributes];
    if (dialogMode === "edit" && attrIndex !== -1) {
      next = next.filter((_, index) => index !== attrIndex);
    }
    setAttributes([...next, { ...newAttr }]);
    setSelectedAttribute(attr);
    setAttrIndex(-1);
    setAttributeDialogOpen(false);
  };

  const handleDialogCancel = () => {
    setSelectedAttribute(attr);
    setAttrIndex(-1);
    setAttributeDialogOpen(false);
  };

  const confirmDeleteAttribute = () => {
    if (attributeToDeleteIndex !== null) {
      setAttributes((prev) => prev.filter((_, index) => index !== attributeToDeleteIndex));
      setAttributeToDeleteIndex(null);
    }
  };

  return (
    <div className="mx-5 space-y-4 pb-8">
      <h1 className="text-2xl font-bold">Placements Settings</h1>

      <ConfigOptionsCard
        title="Statuses"
        description="Configure placement statuses used for workflow and status badge rendering."
        options={activeStatuses}
        setOptions={setActiveStatuses}
        newOption={newStatus}
        setNewOption={setNewStatus}
        addLabel="New status"
      />

      <ConfigOptionsCard
        title="Placement Types"
        description="Suggested placement type values. Stored placement records still keep raw strings."
        options={placementTypes}
        setOptions={setPlacementTypes}
        newOption={newPlacementType}
        setNewOption={setNewPlacementType}
        addLabel="New placement type"
      />

      <ConfigOptionsCard
        title="Fee Types"
        description="Suggested fee type values. Imports can still persist unmatched strings."
        options={feeTypes}
        setOptions={setFeeTypes}
        newOption={newFeeType}
        setNewOption={setNewFeeType}
        addLabel="New fee type"
      />

      <Card>
        <CardHeader>
          <CardTitle>Custom Attributes</CardTitle>
          <CardDescription>
            Configure additional fields for placements. These attributes are available in placement writes and syncs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {canCreateAttributes ? (
            <div className="text-right">
              <Button type="button" onClick={handleAddClick}>
                <PlusIcon className="mr-1 h-4 w-4" /> Add Attribute
              </Button>
            </div>
          ) : isSessionLoading ? (
            <Alert>
              <AlertDescription>Loading session information...</AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <AlertDescription>Only administrators can create placement custom attributes.</AlertDescription>
            </Alert>
          )}

          <AttributesDialog
            open={attributeDialogOpen}
            onOpenChange={setAttributeDialogOpen}
            attribute={selectedAttribute}
            onSave={handleDialogSave}
            onCancel={handleDialogCancel}
            mode={dialogMode}
            optionsList={optionsList}
            entityTypes={entityTypes}
            existingNames={attributes
              .filter((attribute, index) => dialogMode === "add" || index !== attrIndex)
              .map((attribute) => attribute.name.trim())}
            existingIds={attributes
              .filter((attribute, index) => dialogMode === "add" || index !== attrIndex)
              .map((attribute) => attribute.id?.trim() || makeAttributeId(attribute.name))}
          />

          <Table useDynamicHeight={false}>
            <TableHeader>
              <TableRow className="bg-muted-foreground/5">
                <TableHead>Name</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Required</TableHead>
                <TableHead>Unique</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attributes.map((attribute, index) => (
                <TableRow key={index}>
                  <TableCell>{attribute.name}</TableCell>
                  <TableCell>
                    <code>{attribute.id?.trim() || makeAttributeId(attribute.name)}</code>
                  </TableCell>
                  <TableCell>{attribute.type}</TableCell>
                  <TableCell>{attribute.boundaries?.required ? "Yes" : "No"}</TableCell>
                  <TableCell>{attribute.boundaries?.unique ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Trash2 className="h-4 w-4 cursor-pointer" onClick={() => setAttributeToDeleteIndex(index)} />
                      <EditIcon
                        className="h-4 w-4 cursor-pointer text-green-500"
                        onClick={() => handleEditClick(attribute, index)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {update.isError && (
        <Alert variant="destructive">
          <AlertDescription>
            {update.error instanceof Error ? update.error.message : "Failed to save placement settings."}
          </AlertDescription>
        </Alert>
      )}

      <Button type="button" onClick={save} disabled={update.isPending}>
        Save
      </Button>

      <Separator className="my-6" />

      <DeleteConfirmationDialog
        open={attributeToDeleteIndex !== null}
        onOpenChange={(open) => !open && setAttributeToDeleteIndex(null)}
        onConfirm={confirmDeleteAttribute}
        description={`This will permanently delete the attribute "${
          attributeToDeleteIndex !== null ? attributes[attributeToDeleteIndex]?.name : ""
        }". This action cannot be undone.`}
      />
    </div>
  );
}
